import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

export default function MessageColorPalette({ message }) {
    const { t } = useTranslation();
    const [copied, setCopied] = React.useState(null);
    const timer = React.useRef(null);
    React.useEffect(() => {
        setCopied(null);
        return () => clearTimeout(timer.current);
    }, [message?.id]);
    const seen = new Set();
    const colors = (message?.imageDisplayColors || []).filter(color => {
        if (!/^#[a-f\d]{6}$/i.test(color?.hex || '') || seen.has(color.hex.toLowerCase())) return false;
        seen.add(color.hex.toLowerCase());
        return true;
    }).slice(0, 5);
    if (!colors.length) return null;
    const copy = async hex => {
        try {
            await navigator.clipboard.writeText(hex.toLowerCase());
            setCopied(hex);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(null), 1600);
        } catch { setCopied(null); }
    };
    return <Palette aria-label={t('folders:imageColor')} role="group">
        {colors.map((color, index) => <Swatch key={color.hex} type="button" $color={color.hex} $index={index}
            aria-label={`${t('folders:imageColor')} ${color.hex}`} onClick={event => {
                event.stopPropagation(); void copy(color.hex);
            }}>
            <Tooltip>{copied === color.hex ? t('messages:copied') : color.hex.toLowerCase()}</Tooltip>
        </Swatch>)}
        <Status role="status" aria-live="polite">{copied ? t('messages:copied') : ''}</Status>
    </Palette>;
}

const Palette = styled.div`
    display: flex; align-items: center; width: fit-content; max-width: 100%;
    padding: 8px 4px 12px; position: relative; isolation: isolate;
    &:hover > button, &:focus-within > button { margin-left: 5px; }
    &:hover > button:first-child, &:focus-within > button:first-child { margin-left: 0; }
    @media (hover: none) { > button + button { margin-left: 5px; } }
    @media (prefers-reduced-motion: reduce) { > button { transition: none; } }
`;
const Swatch = styled.button`
    position: relative; display: block; flex: 0 0 30px; width: 30px; height: 30px; padding: 0;
    margin-left: ${props => props.$index ? '-12px' : '0'};
    z-index: ${props => props.$index + 1}; cursor: pointer;
    background: ${props => props.$color}; border: 2px solid rgba(255,255,255,.85); border-radius: 50%;
    transition: margin-left 180ms ease, transform 180ms ease;
    &:hover, &:focus-visible { z-index: 10; transform: translateY(-2px); }
    &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
    &:hover > span, &:focus-visible > span { opacity: 1; visibility: visible; }
`;
const Tooltip = styled.span`
    position: absolute; bottom: calc(100% + 9px); left: 50%; transform: translateX(-50%);
    pointer-events: none; white-space: nowrap; background: #121416; color: #eee;
    font: 12px/1.4 ui-monospace, monospace; padding: 5px 8px; border-radius: 7px;
    opacity: 0; visibility: hidden;
    &::after { content: ''; position: absolute; top: 100%; left: 50%; margin-left: -4px;
        border: 4px solid transparent; border-top-color: #121416; }
`;
const Status = styled.span`
    position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%);
`;
